import * as THREE from 'three';
import { PointToPointList } from '../PointToPointList';
import { PointInfo } from '../../WalltoFittingInfo';
import { FurnitureType } from '../FurnitureInfo';
import { PinType } from '../PinInfo';
import { OffsetCalculate } from '../../graphicLibs';

/**
 * 傾き補正
 * */
export class TiltCorrection {

    constructor(private pointList: PointInfo[]) {
        ;
    }

    public execute(): number {
        const rad1 =  this.calculateRadFromArea();
        const [rad2, rad3, rad4] = this.getRadiun();

        const pointList: PointInfo[][] = [];
        pointList.push(new PointToPointList(this.pointList, -rad1).execute());
        pointList.push(new PointToPointList(this.pointList, -rad2).execute());
        pointList.push(new PointToPointList(this.pointList, -rad3).execute());
        pointList.push(new PointToPointList(this.pointList, -rad4).execute());

        // 最適解を求める
        const index = this.optimize(pointList);

        return [rad1, rad2, rad3, rad4][index];
    }

    /**
     * 面積から傾きの角度を求める
     */
    private calculateRadFromArea() {
        let minArea = Infinity;
        let radiun = 0;
        for (let deg = 0; deg < 180; deg++) {
            const minX = new THREE.Vector3();
            const minY = new THREE.Vector3();
            const maxX = new THREE.Vector3();
            const maxY = new THREE.Vector3();
            const rad = deg * Math.PI / 180;
            const pointInfos = new PointToPointList(this.pointList, -rad).execute();
            pointInfos.forEach((info: PointInfo) => {
                minX.min(info.point);
                minY.min(info.point);
                maxX.max(info.point);
                maxY.max(info.point);
            });
            // 面積
            const area = maxX.clone().sub(minX).length() * maxY.clone().sub(minY).length();
            if (minArea > area) {
                minArea = area;
                radiun = rad;
            }
        }
    
        return radiun;
    }

    /**
     * 壁の傾きから家の傾きの角度を求める
     */
    private getRadiun(): number[] {
        const calculate = new OffsetCalculate();
        const radisnList = calculate.getAxisFromPointInfo(this.pointList);
        let radAverage = 0; // 平均値
        let radMedium = 0; // 中央値
        let radMode = 0; // 最頻値
        if (radisnList != null || (radisnList != null && radisnList.length != 0)) {
            radAverage = calculate.calculateAverage(radisnList);
            radMedium = calculate.calculateMedium(radisnList);
            radMode = calculate.calculateMode(radisnList);
        }
        return [radAverage, radMedium, radMode];
    }

    /**
     * 角度の最適解を求める(軸により垂直水平な方の角度を採用する)
     */
    private optimize(pointLists: PointInfo[][]): number {

        const optimizeList: number[][] = [];
        for (let i = 0; i < pointLists.length; i++) {
            optimizeList.push([]);
            for (let j = 0; j < pointLists[i].length; j+=2) {
                const globalX = new THREE.Vector3(1, 0, 0);
                const globalZ = new THREE.Vector3(0, 0, 1);
                const p1 = pointLists[i][j].point;
                const p2 = pointLists[i][(j + 1) % pointLists.length].point;
                const vec3 = p2.clone().sub(p1);

                let rad1 = globalX.angleTo(vec3);
                let rad2 = globalZ.angleTo(vec3);

                const radian1 = (5 * Math.PI) / 180; // 5度の時のラジアン
                const radian2 = (175 * Math.PI) / 180; // 175度の時のラジアン
                const radian3 = (185 * Math.PI) / 180; // 185度の時のラジアン
                const radian4 = (355 * Math.PI) / 180; // 355度の時のラジアン

                // 直線から5度以内の壁であれば、リストに入れる
                if ((0 <= rad1 && rad1 <= radian1) || (radian2 <= rad1 && rad1 <= radian3) || (radian4 <= rad1 && rad1 <= Math.PI)) {
                    optimizeList[i].push(rad1);
                }
                if ((0 <= rad2 && rad2 <= radian1) || (radian2 <= rad2 && rad2 <= radian3) || (radian4 <= rad2 && rad2 <= Math.PI)) {
                    optimizeList[i].push(rad2);
                }
            }
        }

        const counts = optimizeList.map((rad: number[]) => rad.length);

        let maxValue = counts[0];
        let maxIndex = 0;
        for (let i = 1; i < counts.length; i++) {
            if (counts[i] > maxValue) {
              maxValue = counts[i]; // より大きい値が見つかった場合は最大値を更新する
              maxIndex = i; // 最大値のインデックスを更新する
            }
          }
        
          return maxIndex;
    }

    /**
     * オブジェクトが存在する場合は傾きを補正する
     * */
    public furnitureCorrection(furnitureInfo: FurnitureType[], rad: number): FurnitureType[] {
        for (let i = 0; i < furnitureInfo.length; i++) {
            const position = furnitureInfo[i].position.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), -rad);
            furnitureInfo[i].position = position;
            const deg = THREE.MathUtils.radToDeg(rad);
            furnitureInfo[i].rotation = furnitureInfo[i].rotation + deg;
        }

        return furnitureInfo;
    }
    
    /**
     * ピンが存在する場合は傾き補正する
     * */
    public pinCorrection(pinInfo: PinType[], rad: number): PinType[] {
        for (let i = 0; i < pinInfo.length; i++) {
            const position = pinInfo[i].position.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), -rad);
            pinInfo[i].position = position;
            pinInfo[i].dir = pinInfo[i].dir.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), -rad); 
            
            const elements = pinInfo[i].matrix.elements.map((e) => e);
            const xVec = new THREE.Vector3(elements[0], elements[1], elements[2]);
            const yVec = new THREE.Vector3(elements[4], elements[5], elements[6]);
            xVec.applyAxisAngle(new THREE.Vector3(0, 1, 0), -rad); 
            yVec.applyAxisAngle(new THREE.Vector3(0, 1, 0), -rad); 
            
            const matrix = new THREE.Matrix4();
            matrix.elements = [
                xVec.x, xVec.y, xVec.z, elements[3],
                yVec.x, yVec.y, yVec.z, elements[7],
                pinInfo[i].dir.x, pinInfo[i].dir.y, pinInfo[i].dir.z, elements[11],
                position.x, position.y, position.z, elements[15],
            ];
            pinInfo[i].matrix = matrix;
        }

        return pinInfo;
    }

}